import React, {useEffect, useState} from "react";
import {FaFileImport} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import {Modal} from "../modal/modal.jsx";
import Button from "../button/button.jsx";
import aggridUtils from "../../utils/aggrid-utils.js";
import projectUtils from "../../utils/project-utils.js";

const ReferenceImportModal = ({
    open,
    setOpen,
    refetch,
    setIsLoading,
    title,
    columnDefs,
    previewImport,
    createImport,
    buildPayload,
    downloadTemplate,
    getPreviewErrorMessage,
    successMessage,
    successTitle = "",
    importErrorMessage,
    templateErrorMessage
}) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState({rows: [], totals: {}});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const rows = preview.rows ?? [];
    const newCount = rows.filter(({status}) => status === "NEW").length;
    const total = preview.totals?.total ?? rows.length;
    const ignoredCount = preview.totals?.ignored ?? rows.length - newCount;

    useEffect(() => {
        if (!open) {
            setFile(null);
            setPreview({rows: [], totals: {}});
        }
    }, [open]);

    const clear = () => {
        setFile(null);
        setPreview({rows: [], totals: {}});
    };

    const createPreview = (selectedFile) => {
        if (!selectedFile?.name?.toLowerCase().endsWith(".xlsx")) {
            projectUtils.swalErrorMessage("", "Selecione um arquivo Excel válido.");
            return;
        }

        setIsLoading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);

        previewImport(formData)
            .then((data) => {
                setFile(selectedFile);
                setPreview(data);
            })
            .catch((error) => projectUtils.swalErrorMessage("", getPreviewErrorMessage(error), "Ok"))
            .finally(() => setIsLoading(false));
    };

    const selectFile = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        input.onchange = ({target}) => createPreview(target.files?.[0]);
        input.click();
    };

    const handleDownloadTemplate = () => {
        setIsLoading(true);
        downloadTemplate()
            .catch(() => projectUtils.swalErrorMessage("", templateErrorMessage, "Ok"))
            .finally(() => setIsLoading(false));
    };

    const confirmImport = () => {
        if (!file || newCount === 0 || isSubmitting) return;

        setIsSubmitting(true);
        setIsLoading(true);

        createImport(buildPayload(rows))
            .then(({created, ignored}) => {
                const message = successMessage(created, ignored);

                projectUtils.swalSuccessMessage("success", successTitle, message, "Ok", false, "Não", undefined, false, true);

                setOpen(false);
                return refetch();
            })
            .catch(() => projectUtils.swalErrorMessage("", importErrorMessage, "Ok"))
            .finally(() => {
                setIsSubmitting(false);
                setIsLoading(false);
            });
    };

    return (
        <Modal open={open} setOpen={setOpen} centered size="xl" title={title}>
            <Modal.Body className="p-2">
                {!file ? (
                    <div className="h-[calc(100vh-240px)] cursor-pointer select-none" onClick={selectFile} onDrop={(event) => { event.preventDefault(); createPreview(event.dataTransfer.files?.[0]); }} onDragOver={(event) => event.preventDefault()}>
                        <div className="flex h-full items-center justify-center border border-dashed"><h2 className="text-[24px]">Clique aqui ou arraste o arquivo para fazer a importação.</h2></div>
                    </div>
                ) : (
                    <>
                        <div className="ag-theme-quartz h-[calc(100vh-280px)]">
                            <AgGridReact rowData={rows} columnDefs={columnDefs} animateRows={false} suppressCellFocus={true} gridOptions={{localeText: aggridUtils.localization()}} theme={aggridUtils.generalTheme()}/>
                        </div>
                        <div className="flex gap-4 bg-[#efefef] p-2 text-sm">
                            <span>Total: <strong>{total}</strong></span>
                            <span>Novos: <strong className="text-green-700">{newCount}</strong></span>
                            <span>Ignorados: <strong className="text-red-700">{ignoredCount}</strong></span>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex w-full justify-between">
                    <Button target="success" onClick={handleDownloadTemplate}><FaFileImport/> Baixar modelo</Button>
                    <div className="flex gap-2">
                        <Button target="secondary" onClick={clear}>Limpar seleção</Button>
                        <Button onClick={confirmImport} disabled={!file || newCount === 0 || isSubmitting}>Confirmar importação</Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ReferenceImportModal;
